/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {AssetTypeIcon} from '../../../icons/AssetTypeIcon';
import {dlsColor} from '../../../theme/theme';
import {IconSize} from '../../../types/types';
import {LinkedTextButton} from '../../buttons/current/LinkedTextButton';
import Tooltip from '../../tooltip/Tooltip';
import {ClampedTextWithTooltip} from './ClampedTextWithTooltip';
import {useIsTruncated} from './useIsTruncated';

interface AssetSummaryCardIdentityProps {
  assetType: string;
  assetName: string;
  alias?: string;
  onAssetNameClick?: () => void;
  isLink?: boolean;
  // the lines the plain (non-link) name and the alias are clamped to - the
  // Asset Details header keeps each to one ...
  lines?: number;
}

interface ClampedAssetNameButtonProps {
  assetName: string;
  onAssetNameClick?: () => void;
  className: string;
  labelClassName: string;
}

const useStyles = makeStyles(() => ({
  identity : {
    flex       : '1 0 0',
    display    : 'flex',
    minWidth   : 0,
    alignItems : 'flex-start',
    gap        : '8px'
  },
  icon : {
    display    : 'flex',
    flexShrink : 0
  },
  details : {
    flex     : '1 0 0',
    minWidth : 0
  },
  nameButton : {
    height      : 'auto !important',
    paddingLeft : '0 !important',
    width       : '100% !important'
  },
  nameButtonLabel : {
    display         : '-webkit-box',
    overflow        : 'hidden',
    textOverflow    : 'ellipsis',
    textAlign       : 'left',
    WebkitBoxOrient : 'vertical',
    WebkitLineClamp : 2,
    whiteSpace      : 'normal',
    overflowWrap    : 'break-word',
    width           : '100%',
    fontSize        : '20px !important',
    lineHeight      : '22px !important',
    letterSpacing   : '0px !important'
  },
  alias : {
    width         : '100%',
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '1px',
    overflowWrap  : 'break-word',
    color         : dlsColor('grey3')
  },
  nameText : {
    width        : '100%',
    // a long name with no spaces breaks too, so its clamp ellipsis shows ...
    overflowWrap : 'anywhere',
    fontFamily   : 'Roboto',
    fontWeight   : 500,
    fontSize     : '20px',
    lineHeight   : '22px',
    color        : dlsColor('grey2')
  }
}));

/**
 * Wraps `LinkedTextButton` with the same 2-line clamp + truncation-only
 * tooltip behavior as `ClampedTextWithTooltip`, since the Asset Name must
 * both be a Link (per DLS Link specifications) and clamp/truncate.
 */
const ClampedAssetNameButton = ({
  assetName,
  className,
  labelClassName,
  onAssetNameClick
}: ClampedAssetNameButtonProps) => {
  const textRef     = useRef<HTMLSpanElement>(null);
  const isTruncated = useIsTruncated(textRef, [assetName]);

  return (
    <Tooltip title={assetName} renderOnlyChildren={!isTruncated}>
      <LinkedTextButton className={className} disableStableLabel onClick={onAssetNameClick}>
        <span ref={textRef} className={labelClassName}>
          {assetName}
        </span>
      </LinkedTextButton>
    </Tooltip>
  );
};

/**
 * AssetSummaryCardIdentity renders the card's Header Left (Asset Identity):
 * the asset type icon inline with the Asset Name, and the Alias below it
 * when available. Both the name and alias wrap up to 2 lines and truncate
 * with a tooltip beyond that (desktop only, via `Tooltip`'s own mobile
 * gate); the icon stays top-aligned when the name wraps.
 *
 * `isLink` defaults to `true` (the Asset Name is a Link, per DLS Link
 * specifications), which is what the Asset List Card (ION-7392) relies on.
 * Passing `isLink={false}` - used by the ION-7461 Asset Details Drawer's
 * Asset Name & Status header - renders the same clamped/truncated text with
 * no link styling and no hover/visited states at all, per that ticket's
 * "Asset name is no longer a link ... all previous states are removed".
 *
 * ```typescript
 *
 *   import {AssetSummaryCardIdentity} from
 *     '@ion/dls/src/components/assetcard/current/AssetSummaryCardIdentity';
 *   ...
 *
 *   <AssetSummaryCardIdentity assetType={AssetType.Vehicle}
 *     assetName="Vehicle 13 Vision"
 *     alias="004013982 - HUMAN SERVICES - 21-0763"
 *     onAssetNameClick={onAssetNameClick} />
 *
 * ```
 *
 */
export const AssetSummaryCardIdentity = ({
  assetType,
  assetName,
  alias,
  onAssetNameClick,
  isLink = true,
  lines
}: AssetSummaryCardIdentityProps) => {
  const classes = useStyles();

  return (
    <div className={classes.identity}>
      <div className={classes.icon}>
        <AssetTypeIcon assetType={assetType} size={IconSize.medium} />
      </div>
      <div className={classes.details}>
        {isLink ? (
          <ClampedAssetNameButton assetName={assetName}
            onAssetNameClick={onAssetNameClick}
            className={classes.nameButton}
            labelClassName={classes.nameButtonLabel} />
        ) : (
          <ClampedTextWithTooltip className={classes.nameText} lines={lines}>
            {assetName}
          </ClampedTextWithTooltip>
        )}
        {alias && (
          <ClampedTextWithTooltip className={classes.alias} lines={lines}>
            {alias}
          </ClampedTextWithTooltip>
        )}
      </div>
    </div>
  );
};

export default AssetSummaryCardIdentity;
